import React, { useCallback, useEffect, useState } from 'react'
import { message } from 'antd'
import { json2csv } from 'json-2-csv';
import { Link } from 'react-router-dom'
import InstructorSidebar from '../common/instructorSidebar'
import InvoiceModal from '../../../core/common/modal/invoiceModal'
import CreateTransactionModal, {
  CreateTransactionPayload,
} from '../../../core/common/modal/createTransactionModal'
import EditTransactionModal from '../../../core/common/modal/EditTransactionModal'
import Pagination from '../../../core/common/pagination'
import {
  getAdminTransactions,
  AdminTransaction,
  getAdminTransactionDetail,
  AdminTransactionsData,
} from '../../../api/transaction'
import { getCourses } from '../../../api/courses'
import { updateAdminTransaction } from '../../../api/transaction'
import { postAdminTransaction } from '../../../api/transaction'
import { useAuth } from '../../../provider/AuthProvider';

const TransactionList = () => {
  const { user } = useAuth();

  const [transactions, setTransactions] = useState<AdminTransaction[]>([])
  const [
    paginationData,
    setPaginationData,
  ] = useState<AdminTransactionsData | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [currentStatus] = useState<string>('')
  const [search, setSearch] = useState<string>("");
  const [selectedTransactionId, setSelectedTransactionId] = useState<
    number | null
  >(null)
  const [
    selectedTransactionDetail,
    setSelectedTransactionDetail,
  ] = useState<AdminTransaction | null>(null)
  const [loadingDetail, setLoadingDetail] = useState<boolean>(false)
  const [editModalVisible, setEditModalVisible] = useState(false)
  const [editLoading, setEditLoading] = useState(false)
  const [courses, setCourses] = useState([])
  const [
    editTransaction,
    setEditTransaction,
  ] = useState<AdminTransaction | null>(null)
  const [createModalVisible, setCreateModalVisible] = useState(false)
  const [createLoading, setCreateLoading] = useState(false)
  const [selectedType, setSelectedType] = useState<string>('CBT');

  const fetchTransactions = useCallback(
    async (page: number = 1, status: string = '', search: string = '') => {
    setLoading(true)
    try {
      const courseType = user?.role === 'supervisor' ? 'CBT' : '';

      const res = await getAdminTransactions({ page, status, search, course_type: courseType })
      setTransactions(res.data.data)
      setPaginationData(res.data)
    } catch (error) {
      // handle error, bisa pakai toast atau alert
      setTransactions([])
      setPaginationData(null)
    } finally {
      setLoading(false)
    }
    },
    [user?.role],
  )

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      fetchTransactions(currentPage, currentStatus, search)
    }, 500)

    return () => clearTimeout(delayDebounce)
  }, [currentPage, currentStatus, search, fetchTransactions])

  const handleSearchChange = (value: string) => {
    setSearch(value)
    setCurrentPage(1)
  }

  // Fetch detail saat id berubah
  useEffect(() => {
    if (selectedTransactionId) {
      setLoadingDetail(true)
      getAdminTransactionDetail(selectedTransactionId)
        .then((res) => setSelectedTransactionDetail(res.data))
        .catch(() => setSelectedTransactionDetail(null))
        .finally(() => setLoadingDetail(false))
    } else {
      setSelectedTransactionDetail(null)
    }
  }, [selectedTransactionId])

  // Fetch courses for select
  useEffect(() => {
    let isMounted = true

    const fetchCourses = async () => {
      try {
        const type = selectedType || undefined
        const firstPage = await getCourses({ type, limit: 100, page: 1 }, true)
        const firstPageCourses = Array.isArray(firstPage.data)
          ? firstPage.data
          : []
        const lastPage = Number(firstPage.last_page || 1)
        const remainingPages = Array.from(
          { length: Math.max(lastPage - 1, 0) },
          (_, index) => index + 2,
        )

        const remainingResponses = await Promise.all(
          remainingPages.map((page) =>
            getCourses({ type, limit: 100, page }, true),
          ),
        )
        const allCourses = [
          ...firstPageCourses,
          ...remainingResponses.flatMap((response) =>
            Array.isArray(response.data) ? response.data : [],
          ),
        ]

        if (isMounted) {
          setCourses(allCourses)
        }
      } catch {
        if (isMounted) {
          setCourses([])
        }
      }
    }

    fetchCourses()

    return () => {
      isMounted = false
    }
  }, [selectedType])

  // Handler klik eye
  const handleViewDetail = (id: number) => {
    setSelectedTransactionId(null);

    setTimeout(() => {
      setSelectedTransactionId(id);
    }, 100);
  }

  // Handler klik edit
  const handleEdit = (trx: AdminTransaction) => {
    getAdminTransactionDetail(trx.id).then((res) => setEditTransaction(res.data));
    setEditModalVisible(true)
  }

  const handleEditSubmit = async (data: any) => {
    if (!editTransaction) return
    setEditLoading(true)
    try {
      const res = await updateAdminTransaction(editTransaction.id, data)
      // Update list pakai data response
      setTransactions((prev) =>
        prev.map((trx) =>
          trx.id === editTransaction.id ? { ...trx, ...res.data } : trx,
        ),
      )
      setEditModalVisible(false)
    } catch (e) {
      // handle error (bisa pakai toast)
    } finally {
      setEditLoading(false)
    }
  }

  const handleCreate = () => {
    setCreateModalVisible(true)
  }


  const handleCreateSubmit = async (data: CreateTransactionPayload) => {
    setCreateLoading(true)
    try {
      const users = data.users?.length
        ? data.users
        : [{ email: data.email, fullname: data.fullname }]
      const courseIds = data.course_ids?.length
        ? data.course_ids
        : [data.course_id]

      const totalItems = users.length * courseIds.length
      let counter = 1

      for (const userItem of users) {
        for (const courseId of courseIds) {
          const orderId = totalItems > 1
            ? `${data.order_id}-${counter}`
            : data.order_id

          await postAdminTransaction({
            order_id: orderId,
            course_id: courseId,
            email: userItem.email,
            fullname: userItem.fullname,
            total: data.total,
            status: data.status,
            notes: data.notes,
            type: data.type || 'manual',
          })
          counter += 1
        }
      }

      // Refresh transactions after creating new one
      const refreshRes = await getAdminTransactions({ page: currentPage })
      setTransactions(refreshRes.data.data)
      setPaginationData(refreshRes.data)
      setCreateModalVisible(false)
    } catch (e) {
      // handle error (bisa pakai toast)
    } finally {
      setCreateLoading(false)
    }
  }


  // Handle pagination
  const handlePageChange = (page: number) => {
    if (page !== currentPage && !loading) {
      setCurrentPage(page)
    }
  }

  const handlePrevPage = () => {
    if (paginationData?.prev_page_url && currentPage > 1 && !loading) {
      setCurrentPage(currentPage - 1)
    }
  }

  const handleNextPage = () => {
    if (
      paginationData?.next_page_url &&
      currentPage < paginationData.last_page &&
      !loading
    ) {
      setCurrentPage(currentPage + 1)
    }
  }

  const handleDownloadCsv = async (page: number = 1, status: string, search: string) => {
    try {
      const res = await getAdminTransactions({
        page,
        status,
        search,
        export: true,
      });

      const rawData = Array.isArray(res?.data)
        ? res.data
        : (res?.data as AdminTransactionsData)?.data ?? [];

      const csvData = rawData.map((item: any) => ({
        'Invoice ID': item.order_id || '-',
        'Tanggal': new Date(item.created_at).toLocaleDateString('id-ID'),
        'Nama User': item.user ? item.user.fullname : '-',
        'Email User': item.email || '-',
        'Whatsapp': item.user ? item.user.phone_number : '-',
        'Jenis Konten': item.course?.type || '-',
        'Judul Konten': item.course?.title || '-',
        'Amount': `Rp ${Number(item.total).toLocaleString('id-ID')}`,
        'Status Transaksi': item.status === 'paid' ? 'Berhasil' : item.status,
      }));

      const csv = await json2csv(csvData);

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = `transactions-${new Date().toLocaleDateString('id-ID')}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('❌ Error generating CSV:', error);
    }
  };

  return (
    <>
      <div className="content">
        <div className="container">
          <div className="row">
            {/* sidebar */}
            <InstructorSidebar />
            {/* sidebar */}
            <div className="col-lg-9">
              <div className="page-title d-flex align-items-center justify-content-between">
                <h5>Transaction</h5>
                <div className='d-flex align-items-center justify-content-end'>
                  <Link
                    to="#"
                    className="btn btn-primary rounded-pill ms-3"
                    onClick={() => handleDownloadCsv(currentPage, currentStatus, search)}
                  >
                    <i className="isax isax-add-circle me-2 fs-10" />
                    Download CSV
                  </Link>


                  <Link
                    to="#"
                    className="btn btn-secondary rounded-pill ms-3"
                    onClick={handleCreate}
                  >
                    <i className="isax isax-add-circle me-2 fs-10" />
                    Add Transaction
                  </Link>
                </div>
              </div>
              <div className="row justify-content-end">
                <div className="col-md-4">
                  <div className="input-icon mb-3">
                    <span className="input-icon-addon">
                      <i className="isax isax-search-normal-14" />
                    </span>
                    <input
                      type="search"
                      value={search}
                      onChange={e => handleSearchChange(e.target.value)}
                      placeholder="Search..."
                      className="form-control form-control-md"
                    />
                  </div>
                </div>
              </div>
              <div className="table-responsive custom-table">
                <table className="table">
                  <thead className="thead-light">
                    <tr>
                      <th>Order ID</th>
                      <th>Date</th>
                      <th>Course</th>
                      <th>Email</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={7} className="text-center">
                          Loading...
                        </td>
                      </tr>
                    ) : transactions.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="text-center">
                          No transactions found
                        </td>
                      </tr>
                    ) : (
                      transactions.map((trx) => (
                        <tr key={trx.id}>
                          <td>
                            <Link
                              to="#"
                              className="text-primary"
                              data-bs-toggle="modal"
                              data-bs-target="#view_invoice"
                              onClick={() => handleViewDetail(trx.id)}
                            >
                              {trx.order_id}
                            </Link>
                          </td>
                          <td>
                            {new Date(trx.created_at).toLocaleDateString()}
                          </td>
                          <td className='text-wrap'>{trx.course?.title || '-'}</td>
                          <td>{trx.email}</td>
                          <td>{trx.total}</td>
                          <td>
                            <span
                              className={`badge ${trx.status === 'paid' ? 'bg-success' : trx.status === 'pending' ? 'bg-info' : 'bg-danger'
                                } d-inline-flex align-items-center me-1`}
                            >
                              <i className="fa-solid fa-circle fs-5 me-1" />
                              {trx.status}
                            </span>
                          </td>
                          <td>
                            <div className="d-flex align-items-center">
                              <Link
                                to="#"
                                className="d-inline-flex fs-14 me-1 action-icon"
                                data-bs-toggle="modal"
                                data-bs-target="#view_invoice"
                                onClick={() => handleViewDetail(trx.id)}
                              >
                                <i className="isax isax-eye" />
                              </Link>
                              {/* <Link
                                to="#"
                                className="d-inline-flex fs-14 action-icon"
                              >
                                <i className="isax isax-import" />
                              </Link> */}
                              <Link
                                to="#"
                                className="d-inline-flex fs-14 action-icon"
                                onClick={() => handleEdit(trx)}
                              >
                                <i className="isax isax-edit" />
                              </Link>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {paginationData && (
                <Pagination
                  paginationData={paginationData}
                  onPageChange={handlePageChange}
                  onPrevPage={handlePrevPage}
                  onNextPage={handleNextPage}
                  loading={loading}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <InvoiceModal
        transaction={loadingDetail ? null : selectedTransactionDetail}
      />
      <CreateTransactionModal
        visible={createModalVisible}
        onClose={() => setCreateModalVisible(false)}
        onSubmit={handleCreateSubmit}
        courses={courses}
        loading={createLoading}
        setSelectedType={setSelectedType}
        selectedType={selectedType}
      />
      <EditTransactionModal
        visible={editModalVisible}
        onClose={() => setEditModalVisible(false)}
        onSubmit={handleEditSubmit}
        transaction={editTransaction}
        courses={courses}
        loading={editLoading}
      />
    </>
  )
}

export default TransactionList
